import { useEffect, useMemo } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import axios from "axios";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { IUsersAdministrationUserModel } from "../../usersList/models/usersAdministrationUserModel";
import { mergeUsersConfirmMessage } from "../helpers/mergeUsersConfirmMessage";
import {
  IMergeUsersFormModel,
  isMergeConfirmationWordValid,
  IUserMergePreview,
  MERGE_PREVIEW_COUNT_LABELS,
  mergeUsersFormDefaultValues,
  mergeUsersFormSchema,
} from "../models/mergeUsersFormModel";

interface IUseMergeUsersDialogProps {
  /** The account being viewed: it is kept and receives everything. */
  userId?: number;
  visible: boolean;
  onHide: () => void;
}

export interface IMergePreviewCountItem {
  label: string;
  count: number;
}

/** "Иван Петров (0888 123 456)", the email when there is no phone, and " (изтрит)" for a deleted account. */
export const formatMergeCandidateLabel = (
  user: IUsersAdministrationUserModel,
): string => {
  const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
  const contact = user.phoneNumber?.trim() || user.email?.trim();
  return `${name}${contact ? ` (${contact})` : ""}${user.isDeleted ? " (изтрит)" : ""}`;
};

/**
 * "Сливане на акаунти": pick another client account of the studio, see what would move
 * from it (and what blocks the merge), type the confirmation word, confirm once more.
 * The picked account is merged INTO the viewed one and then deleted.
 */
export const useMergeUsersDialog = ({
  userId,
  visible,
  onHide,
}: IUseMergeUsersDialogProps) => {
  const form = useForm<IMergeUsersFormModel>({
    defaultValues: mergeUsersFormDefaultValues,
    resolver: yupResolver(mergeUsersFormSchema),
    mode: "onChange",
  });

  useEffect(() => {
    if (!visible) {
      form.reset(mergeUsersFormDefaultValues);
    }
  }, [visible, form]);

  // The studio's clients, deleted ones included: a duplicate someone already deleted is a
  // typical account to merge back in.
  const { data: users, isLoading: areCandidatesLoading } = useApiQuery<
    IUsersAdministrationUserModel[]
  >({
    queryKey: [endpointsMetadata.user.getAll],
    endpoint: endpointsMetadata.user.getAll,
    enabled: visible,
    globalIsLoading: false,
  });

  const candidateOptions: ISelectFieldModel[] = useMemo(
    () =>
      (users ?? [])
        .filter((user) => user.id !== userId)
        .map((user) => ({
          id: user.id,
          name: formatMergeCandidateLabel(user),
        })),
    [users, userId],
  );

  const sourceUserId = form.watch("sourceUserId");
  const confirmationWord = form.watch("confirmationWord") ?? "";

  // A word typed for one account must not confirm another one.
  useEffect(() => {
    form.setValue("confirmationWord", "");
  }, [sourceUserId, form]);
  const hasSource = visible && !!userId && !!sourceUserId;

  const {
    data: preview,
    isFetching: isPreviewFetching,
    error: previewError,
  } = useApiQuery<IUserMergePreview>({
    queryKey: [endpointsMetadata.user.getMergePreview, userId, sourceUserId],
    endpoint: `${endpointsMetadata.user.getMergePreview}?targetUserId=${userId}&sourceUserId=${sourceUserId}`,
    enabled: hasSource,
    globalIsLoading: false,
    // A refusal (staff account, another studio's client, ...) will not change on retry.
    retry: false,
  });

  const shownPreview = hasSource ? preview : undefined;
  const isPreviewLoading = hasSource && isPreviewFetching && !shownPreview;

  const previewErrorMessage = useMemo(() => {
    if (!hasSource || !previewError || isPreviewFetching) {
      return undefined;
    }
    if (axios.isAxiosError(previewError)) {
      const message = (previewError.response?.data as { message?: unknown })
        ?.message;
      if (typeof message === "string" && message) {
        return message;
      }
    }
    return "Неуспешно зареждане на прегледа. Моля опитайте отново.";
  }, [hasSource, previewError, isPreviewFetching]);

  const previewCounts: IMergePreviewCountItem[] = useMemo(
    () =>
      shownPreview?.counts
        ? MERGE_PREVIEW_COUNT_LABELS.map(({ key, label }) => ({
            label,
            count: shownPreview.counts[key] ?? 0,
          })).filter((item) => item.count > 0)
        : [],
    [shownPreview],
  );

  const conflicts = shownPreview?.conflicts ?? [];
  const isConfirmationWordValid = isMergeConfirmationWordValid(confirmationWord);

  const canMerge =
    hasSource &&
    !!shownPreview &&
    !isPreviewFetching &&
    shownPreview.canMerge &&
    conflicts.length === 0 &&
    isConfirmationWordValid;

  const { mutate, isLoading } = useApiMutation();

  const handleMerge = () => {
    if (!canMerge || !userId || !shownPreview) {
      return;
    }

    showCustomConfirmDialog(
      mergeUsersConfirmMessage(shownPreview.sourceName),
      () => {
        // Closed first, so the global loader is not hidden behind the dialog while the
        // merge runs. Closing also clears the form; a refusal still shows its error toast.
        onHide();
        mutate({
          endpoint: endpointsMetadata.user.mergeUsers,
          method: "post",
          globalIsLoading: true,
          payload: {
            targetUserId: userId,
            sourceUserId,
          },
          invalidateKeys: [
            [endpointsMetadata.user.getUser, String(userId)],
            [endpointsMetadata.user.getAll],
            [endpointsMetadata.userAuditLog.getUserHistory, userId],
          ],
        });
      },
      () => {},
      "warning",
      "Да, слей",
      "Не",
    );
  };

  return {
    form,
    candidateOptions,
    areCandidatesLoading,
    sourceUserId,
    preview: shownPreview,
    previewCounts,
    isPreviewLoading,
    previewErrorMessage,
    canMerge,
    isLoading,
    handleMerge,
  };
};
